import { Box, SxProps, Theme } from '@mui/material'
import { SUITABILITY_GRADE_LABELS, SuitabilityGrade } from '../types'

type BadgeSize = 'xs' | 'sm' | 'md' | 'lg'

interface SuitabilityBadgeConfig {
  bg: string
  color: string
  label: string
}

const gradeConfigMap: Record<SuitabilityGrade, SuitabilityBadgeConfig> = {
  A: { bg: '#E6F4EA', color: '#1E7E34', label: SUITABILITY_GRADE_LABELS.A },
  B: { bg: '#EAF4E6', color: '#4CAF50', label: SUITABILITY_GRADE_LABELS.B },
  C: { bg: '#FFF8E1', color: '#F9A825', label: SUITABILITY_GRADE_LABELS.C },
  D: { bg: '#FFF3E0', color: '#EF6C00', label: SUITABILITY_GRADE_LABELS.D },
  E: { bg: '#FFEBEE', color: '#C62828', label: SUITABILITY_GRADE_LABELS.E },
}

type SuitabilityBadgeProps = (
  | { grade: SuitabilityBadgeConfig; letterGrade?: never }
  | { letterGrade: SuitabilityGrade; grade?: never }
) & {
  size?: BadgeSize
  label?: string
  showDot?: boolean
  sx?: SxProps<Theme>
}

const sizeConfig: Record<
  BadgeSize,
  {
    px: number
    py: number
    gap: string
    fontSize: number
    dotSize: number
    borderRadius: number
  }
> = {
  xs: { px: 0.75, py: 0.25, gap: '4px', fontSize: 10, dotSize: 5, borderRadius: 1 },
  sm: { px: 1.25, py: 0.5, gap: '6px', fontSize: 12, dotSize: 7, borderRadius: 1.25 },
  md: { px: 1.5, py: 0.75, gap: '6px', fontSize: 14, dotSize: 8, borderRadius: 1.5 },
  lg: { px: 2, py: 1, gap: '8px', fontSize: 16, dotSize: 10, borderRadius: 1.5 },
}

export const SuitabilityBadge = ({
  grade,
  letterGrade,
  size = 'sm',
  label,
  showDot = false,
  sx,
}: SuitabilityBadgeProps) => {
  const resolvedGrade = grade ??
    gradeConfigMap[letterGrade] ?? {
      bg: '#ECECEC',
      color: '#757575',
      label: letterGrade ?? '?',
    }
  const { px, py, gap, fontSize, dotSize, borderRadius } = sizeConfig[size]

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap,
        px,
        py,
        borderRadius,
        background: resolvedGrade.bg,
        color: resolvedGrade.color,
        fontSize,
        fontWeight: 500,
        ...sx,
      }}
    >
      {showDot && (
        <Box
          sx={{
            width: dotSize,
            height: dotSize,
            borderRadius: '50%',
            background: resolvedGrade.color,
            flexShrink: 0,
          }}
        />
      )}
      {label ?? resolvedGrade.label}
    </Box>
  )
}
